/**
 * CREATIVE DETONATOR - Video Generation Module
 * Handles video creation, polling, and persistence
 *
 * v71 - Complete persistence fix + enhanced logging:
 * - Multi-strategy UUID resolution (memory → sessionStorage → idea object → localStorage)
 * - Persistent idMapping that survives page refresh
 * - Pending sync queue for videos that fail to save
 * - Visual feedback for save status
 * - Retry logic with exponential backoff
 * v72 - Self-rendering module: injects its own HTML on init
 * v73 - Reorder modal: Video Prompt above Video Settings, tighter spacing
 * v74 - Active generation persistence: videos survive page refresh
 * - Stores requestId/modelId in localStorage when generation starts
 * - Resumes polling on page load for any active generations
 * - 10-minute timeout for orphaned generations
 * v76 - Canvas refresh: update canvas left-side preview when video completes
 * - Checks if IdeaCanvas is open for this idea
 * - Calls IdeaCanvas.refresh() to update left column preview
 * v77 - UI freeze fix: Added cleanupVideoGenState() to reset UI after video completion
 * - Cleans up modal state, body overflow, and lingering overlays
 * - Added window.resetVideoGenUI() debug utility for manual reset
 * - Uses updateIdea() instead of refresh() for proper canvas update
 * v78 - REFACTOR-07: JobManager integration for persistence
 * - Replaced manual localStorage with JobManager from async-utils.js
 * - Added migration logic for legacy cd_active_video_generations storage
 * - saveActiveGeneration() now uses JobManager.register()
 * - clearActiveGeneration() now uses JobManager.cancel()
 * - resumeActiveGenerations() now uses JobManager.resume()
 * - Polling logic unchanged to preserve all business logic
 */

import { GeneratorState, API_CONFIG, persistSessionState } from '../core/state.js';
import { getSessionToken } from '../../auth/js/client.js';
import { getIdeaImageUrl } from './image-generation.js';
import { injectModalHtml } from '../../../js/shared/dom-utils.js';
import { onDocument, cleanupScope, emit, VideoEvents, IdeaEvents } from '../../../js/core/events.js';
import { VIDEO_EVENTS, IDEA_EVENTS } from '../../../js/core/event-types.js';
import { showError } from '../../../js/core/error-handler.js';
import { createLogger } from '../../../js/core/logger.js';
import { getContextBarHTML, updateContextBar, setupContextToggle } from '../../../js/shared/modal-context-bar.js';
import { isValidUUID } from '../../../js/core/uuid-utils.js';
import { downloadVideo as downloadVideoCore } from '../../../js/core/download.js';
import { animateCoinSpend, updateCreditDisplays } from '../../../js/shared/credit-animation.js';
// REFACTOR-07: Use centralized JobManager for persistence (survives page refresh)
import { JobManager } from '../../../js/core/async-utils.js';
// Centralized pricing - Single Source of Truth (see frontend/js/core/pricing.js)
import { getVideoCost, VIDEO_COSTS, formatCredits } from '../../../js/core/pricing.js';
// Icons and version history for showGeneratedVideo card UI
import { Icons } from '../../../js/shared/icons.js';
import { renderHistoryButton } from './version-history.js';

const log = createLogger('VideoGen');

/**
 * Refresh credit balance from server and update all displays
 */
async function refreshCreditsFromServer() {
  try {
    const token = await getSessionToken();
    if (!token) return;

    const response = await fetch(`${API_CONFIG.baseUrl}/api/credits`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (response.ok) {
      const data = await response.json();
      if (data.success && typeof data.credits === 'number') {
        updateCreditDisplays(data.credits);
        log.debug('Credits refreshed:', data.credits);
      }
    }
  } catch (err) {
    log.warn('Failed to refresh credits:', err.message);
  }
}

// ============================================================================
// HTML TEMPLATES - Self-rendering modals
// ============================================================================

const VIDEO_GEN_MODAL_HTML = `
<div class="modal-overlay" id="video-gen-modal">
  <div class="retro-modal video-gen-modal">
    <div class="window-titlebar modal-titlebar">
      <div class="window-close modal-close-btn" id="video-gen-close" aria-label="Close modal"></div>
      <div class="window-title">◆ MAKE VIDEO</div>
    </div>
    <div class="modal-idea-subtitle" id="video-gen-idea-title"></div>
    <div class="modal-body">
      <!-- Source Image Preview -->
      <div class="video-gen-section video-gen-preview">
        <label class="video-gen-label" id="video-source-label">SOURCE IMAGE</label>
        <div class="video-source-images">
          <div class="video-source-image-container" id="video-start-image-container">
            <span class="image-label" id="video-start-label" style="display: none;">START</span>
            <img id="video-source-image" src="" alt="Start frame" />
          </div>
          <div class="video-source-image-container" id="video-end-image-container" style="display: none;">
            <span class="image-label">END</span>
            <img id="video-end-image" src="" alt="End frame" />
          </div>
        </div>
      </div>

      <!-- Video Prompt (moved above settings) -->
      <div class="video-gen-section">
        <label class="video-gen-label">VIDEO PROMPT</label>
        <textarea class="video-gen-textarea" id="video-gen-prompt" rows="5" spellcheck="false" placeholder="Describe the motion and action for the video..."></textarea>
      </div>

      <!-- Video Settings -->
      <div class="video-gen-section">
        <label class="video-gen-label">VIDEO SETTINGS</label>

        <!-- Model Selector Row -->
        <div class="video-settings-row">
          <span class="setting-label">Model:</span>
          <select id="video-model-selector" class="video-gen-select-inline">
            <!-- Populated by JavaScript -->
          </select>
        </div>

        <!-- Duration Row (dynamic) -->
        <div class="video-settings-row" id="video-duration-row">
          <span class="setting-label">Duration:</span>
          <div id="video-duration-container">
            <span class="setting-badge is-fixed">8 seconds</span>
          </div>
        </div>

        <!-- Resolution Toggle (dynamic) -->
        <div class="video-settings-row" id="video-resolution-row">
          <span class="setting-label">Resolution:</span>
          <div class="toggle-buttons" id="video-resolution-toggle">
            <button class="toggle-btn active" data-value="720p">720p</button>
            <button class="toggle-btn" data-value="1080p">1080p</button>
          </div>
        </div>

        <!-- Audio Toggle -->
        <div class="video-settings-row">
          <span class="setting-label">Audio:</span>
          <div class="toggle-buttons" id="video-audio-toggle">
            <button class="toggle-btn active" data-value="true">ON</button>
            <button class="toggle-btn" data-value="false">OFF</button>
          </div>
        </div>

        <!-- Aspect Ratio Row (Dynamic) -->
        <div class="video-settings-row" id="video-aspect-row" style="display: none;">
          <span class="setting-label">Aspect:</span>
          <div class="toggle-buttons" id="video-aspect-toggle">
            <!-- Populated by JavaScript -->
          </div>
        </div>

        <!-- Camera Fixed Row (Seedance only) -->
        <div class="video-settings-row" id="video-camera-fixed-row" style="display: none;">
          <span class="setting-label">Camera:</span>
          <div class="toggle-buttons" id="video-camera-fixed-toggle">
            <button class="toggle-btn" data-value="true">FIXED</button>
            <button class="toggle-btn active" data-value="false">MOVE</button>
          </div>
        </div>

        <!-- Cost Display - FIX UI-4: Show both USD and credits -->
        <div class="video-cost-display">
          <span class="cost-label">Estimated cost:</span>
          <span class="cost-value" id="video-cost-value">$3.20</span>
          <span class="cost-credits" id="video-cost-credits">(320 C)</span>
        </div>
      </div>
      ${getContextBarHTML('video-gen')}
      <div class="prompt-tip-blue">
        <span class="tip-icon-blue">i</span>
        <span class="tip-text-blue">Video generation takes 60-180 seconds. The video will appear in the idea card when ready.</span>
      </div>
    </div>
    <div class="modal-footer video-gen-footer">
      <button class="btn btn-ghost" id="video-gen-cancel">CANCEL</button>
      <button class="btn btn-primary btn-generate-video" id="video-gen-submit">
        <span class="btn-text">GENERATE VIDEO <span class="btn-credits" id="video-gen-btn-credits"><span class="pixel-coin-static xs"></span> <span class="btn-credits-value">320</span></span></span>
        <span class="btn-loading" style="display:none;">GENERATING...</span>
      </button>
    </div>
  </div>
</div>
`;

const VIDEO_PREVIEW_MODAL_HTML = `
<div class="modal-overlay" id="video-preview-modal">
  <div class="retro-modal video-preview-modal">
    <div class="window-titlebar modal-titlebar">
      <div class="window-close modal-close-btn" id="video-preview-close" aria-label="Close modal"></div>
      <div class="window-title">◆ VIDEO PREVIEW</div>
    </div>
    <div class="modal-body video-preview-body">
      <div class="video-preview-container">
        <video id="video-preview-player" class="video-preview-player" controls preload="metadata">
          Your browser doesn't support video playback.
        </video>
      </div>
    </div>
    <div class="modal-footer video-preview-footer">
      <button class="btn btn-ghost" id="video-preview-download">
        <svg viewBox="0 0 12 12" width="12" height="12" style="margin-right:6px;">
          <path d="M6 0v8M2 5l4 5 4-5" stroke="currentColor" stroke-width="2" fill="none"/>
        </svg>
        DOWNLOAD
      </button>
    </div>
  </div>
</div>
`;

// ============================================================================
// STATE
// ============================================================================

const videoGenerations = {};
const pollingIntervals = {};
let configCache = null;

// Pending video syncs for videos that couldn't be saved immediately
const pendingVideoSyncs = {};

// REFACTOR-07: Active generation persistence now handled by JobManager from async-utils.js
// Old storage key for migration purposes only
const LEGACY_GENERATIONS_KEY = 'cd_active_video_generations';

// Current Modal State
let currentIdeaId = null;
let currentImageUrl = null;
let currentEndImageUrl = null;
let currentModelId = null;
let currentDuration = '8';
let currentResolution = '720p';
let currentAspectRatio = '16:9';
let currentAudioEnabled = true;
let currentCameraFixed = false;

// Scene context (when generating video for a scene)
let currentSceneContext = null; // { type: 'scene', sceneSeq, startFrameUrl, endFrameUrl, ... }

// NOTE: UUID validation now uses shared isValidUUID() from uuid-utils.js

// ============================================================================
// COMPOSITE KEY GENERATION (Task 102: Fix concurrent generation collisions)
// ============================================================================

/**
 * Generate a unique key for tracking concurrent generations
 * Prevents state collisions when multiple scene videos generate simultaneously
 * @param {string} ideaId - The idea ID
 * @param {Object|null} sceneContext - Scene context if generating for a scene
 * @returns {string} - Composite key for state tracking
 */
function getGenerationKey(ideaId, sceneContext = null) {
  if (sceneContext?.type === 'scene' && sceneContext?.sceneSeq != null) {
    return `${ideaId}_scene_${sceneContext.sceneSeq}`;
  }
  return String(ideaId);
}

/**
 * Extract ideaId from a composite key
 * @param {string} genKey - The composite generation key
 * @returns {string} - The original idea ID
 */
function extractIdeaIdFromKey(genKey) {
  // Handle both "uuid" and "uuid_scene_N" formats
  const parts = genKey.split('_scene_');
  return parts[0];
}

/**
 * Extract scene sequence from a composite key
 * @param {string} genKey - The composite generation key
 * @returns {number|null} - Scene sequence number or null if not a scene key
 */
function extractSceneSeqFromKey(genKey) {
  const parts = genKey.split('_scene_');
  if (parts.length > 1) {
    return parseInt(parts[1], 10);
  }
  return null;
}

// API Endpoints
const ENDPOINTS = {
  config: '/api/video-gen/config',
  submit: '/api/video-gen/submit',
  status: (m, r) => `/api/video-gen/status/${encodeURIComponent(m)}/${r}`,
  result: (m, r) => `/api/video-gen/result/${encodeURIComponent(m)}/${r}`
};

function getApiUrl(endpoint) {
  return API_CONFIG.baseUrl + endpoint;
}

// ============================================================================
// INITIALIZATION
// ============================================================================

export async function initVideoGeneration() {
  // Self-render: inject modal HTML if not already present
  injectModalHtml('video-gen-modal', VIDEO_GEN_MODAL_HTML);
  injectModalHtml('video-preview-modal', VIDEO_PREVIEW_MODAL_HTML);

  await loadConfig();
  initModalEvents();
  initCardEvents();
  initVideoPreviewModal();

  // Initialize video cache if missing
  if (!GeneratorState.preloadedVideos) {
    GeneratorState.preloadedVideos = {};
  }

  // Load persisted idMapping from sessionStorage
  loadPersistedIdMapping();

  // Load pending video syncs
  loadPendingVideoSyncs();

  // Process any pending syncs if we have mappings
  if (Object.keys(GeneratorState.idMapping || {}).length > 0) {
    await processPendingVideoSyncs();
  }

  // Resume any active generations that were interrupted by page refresh
  await resumeActiveGenerations();

  log.debug(' Initialized');
}

async function loadConfig() {
  try {
    const res = await fetch(getApiUrl(ENDPOINTS.config));
    const data = await res.json();
    if (data.success) {
      configCache = data.config;
      currentModelId = configCache.defaultModel;
    }
  } catch (e) {
    log.error(' Config load failed', e);
  }
}

// ============================================================================
// ID MAPPING PERSISTENCE
// ============================================================================

/**
 * Load persisted idMapping from sessionStorage (with localStorage backup)
 * Task 113: Added localStorage backup to survive browser restarts
 * Called on init to restore mapping after page refresh
 */
function loadPersistedIdMapping() {
  try {
    // Try sessionStorage first (current session)
    let stored = sessionStorage.getItem('cd_id_mapping');

    // Fall back to localStorage backup if sessionStorage is empty
    // This handles the case where browser was closed during active generation
    if (!stored) {
      stored = localStorage.getItem('cd_id_mapping_backup');
      if (stored) {
        log.debug(' Recovered idMapping from localStorage backup');
      }
    }

    if (stored) {
      const mapping = JSON.parse(stored);
      GeneratorState.idMapping = {
        ...GeneratorState.idMapping,
        ...mapping
      };
      log.debug(' Loaded idMapping from storage:', Object.keys(mapping).length, 'entries');
    }
  } catch (e) {
    log.warn(' Could not load idMapping from storage:', e);
  }
}

/**
 * Get the current idMapping, checking memory first then sessionStorage
 * @returns {Object} The ID mapping object
 */
function getIdMapping() {
  // Try memory first (fastest)
  if (GeneratorState.idMapping && Object.keys(GeneratorState.idMapping).length > 0) {
    return GeneratorState.idMapping;
  }

  // Fall back to sessionStorage
  try {
    const stored = sessionStorage.getItem('cd_id_mapping');
    if (stored) {
      const mapping = JSON.parse(stored);
      // Restore to memory for faster access
      GeneratorState.idMapping = mapping;
      return mapping;
    }
  } catch (e) {
    log.warn(' Could not read id mapping from storage:', e);
  }

  return {};
}

/**
 * Save idMapping to memory, sessionStorage, and localStorage backup
 * Task 113: Added localStorage backup to survive browser restarts
 * @param {Object} mapping - New mapping entries to merge
 */
export function saveIdMapping(mapping) {
  GeneratorState.idMapping = {
    ...GeneratorState.idMapping,
    ...mapping
  };
  try {
    // Session storage for current session
    sessionStorage.setItem('cd_id_mapping', JSON.stringify(GeneratorState.idMapping));
    // Also persist to localStorage for cross-session recovery
    localStorage.setItem('cd_id_mapping_backup', JSON.stringify(GeneratorState.idMapping));
    log.debug(' idMapping persisted:', Object.keys(GeneratorState.idMapping).length, 'entries');
  } catch (e) {
    log.warn(' Could not save id mapping to storage:', e);
  }
}

// ============================================================================
// MULTI-STRATEGY UUID RESOLUTION
// ============================================================================

/**
 * Resolve any ID format to a database UUID
 * Uses multiple strategies to find the correct UUID:
 * 1. Check if already UUID
 * 2. Check memory idMapping
 * 3. Check sessionStorage idMapping
 * 4. Search in generatedIdeas for _dbId property
 * 5. Check localStorage (Dashboard use case)
 *
 * @param {string} ideaId - The idea ID (may be temp ID or UUID)
 * @returns {string|null} - Database UUID or null if not found
 */
function resolveToUUID(ideaId) {
  const id = String(ideaId);

  // Strategy 1: Already a UUID (uses shared uuid-utils)
  if (isValidUUID(id)) {
    log.debug(` ID is already UUID: ${id}`);
    return id;
  }

  // Strategy 2: Check idMapping (memory + sessionStorage)
  const mapping = getIdMapping();
  if (mapping[id]) {
    log.debug(` Found in idMapping: ${id} → ${mapping[id]}`);
    return mapping[id];
  }

  // Strategy 3: Search in generatedIdeas for _dbId property
  if (GeneratorState.generatedIdeas) {
    for (const group of GeneratorState.generatedIdeas) {
      const idea = group.ideas?.find(i =>
        String(i.id) === id ||
        String(i._tempId) === id
      );
      if (idea) {
        // Check if idea has a _dbId from previous save
        if (idea._dbId && isValidUUID(idea._dbId)) {
          log.debug(` Found _dbId on idea: ${id} → ${idea._dbId}`);
          return idea._dbId;
        }
        // Check if the idea's ID itself is now a UUID (after save/reload)
        if (isValidUUID(idea.id)) {
          log.debug(` Idea ID is now UUID: ${idea.id}`);
          return idea.id;
        }
      }
    }
  }

  // Strategy 4: Check localStorage for Dashboard's persisted mapping
  try {
    const dashMapping = localStorage.getItem('cd_dashboard_id_mapping');
    if (dashMapping) {
      const parsed = JSON.parse(dashMapping);
      if (parsed[id]) {
        log.debug(` Found in localStorage: ${id} → ${parsed[id]}`);
        return parsed[id];
      }
    }
  } catch (e) {
    // Ignore localStorage errors
  }

  log.warn(` Could not resolve UUID for: ${id}`);
  return null;
}

// ============================================================================
// PENDING VIDEO SYNC QUEUE
// ============================================================================

/**
 * Load pending video syncs from sessionStorage
 */
function loadPendingVideoSyncs() {
  try {
    const stored = sessionStorage.getItem('cd_pending_videos');
    if (stored) {
      Object.assign(pendingVideoSyncs, JSON.parse(stored));
      const count = Object.keys(pendingVideoSyncs).length;
      if (count > 0) {
        log.debug(` Loaded ${count} pending video syncs`);
      }
    }
  } catch (e) {
    log.warn(' Could not load pending videos:', e);
  }
}

/**
 * Queue a video for later sync when the idea gets saved
 * @param {string} ideaId - The idea ID
 * @param {string} videoUrl - The video URL
 * @param {Object} meta - Video metadata
 */
function queueVideoForSync(ideaId, videoUrl, meta) {
  pendingVideoSyncs[ideaId] = {
    videoUrl,
    meta,
    timestamp: Date.now()
  };

  // Persist to sessionStorage
  try {
    sessionStorage.setItem('cd_pending_videos', JSON.stringify(pendingVideoSyncs));
  } catch (e) {
    log.warn(' Could not persist pending videos');
  }

  log.debug(` Queued video for later sync: ${ideaId}`);
}

/**
 * Process pending video syncs
 * Called when idMapping updates or on init if mappings exist
 */
export async function processPendingVideoSyncs() {
  const pending = { ...pendingVideoSyncs };
  const pendingCount = Object.keys(pending).length;

  if (pendingCount === 0) return;

  log.debug(` Processing ${pendingCount} pending video syncs...`);

  for (const [ideaId, data] of Object.entries(pending)) {
    const dbId = resolveToUUID(ideaId);
    if (dbId) {
      log.debug(` Retrying pending video sync for ${ideaId}`);
      const success = await saveVideoToDatabaseInternal(dbId, ideaId, data.videoUrl, data.meta);
      if (success) {
        delete pendingVideoSyncs[ideaId];
      }
    }
  }

  // Update storage
  try {
    sessionStorage.setItem('cd_pending_videos', JSON.stringify(pendingVideoSyncs));
  } catch (e) {}
}

// ============================================================================
// ACTIVE GENERATION PERSISTENCE (Survives Page Refresh)
// REFACTOR-07: Now uses centralized JobManager from async-utils.js
// ============================================================================

/**
 * Save active generation to localStorage via JobManager
 * Called when starting a new video generation
 * @param {string} genKey - Composite generation key (ideaId or ideaId_scene_N)
 * @param {string} modelId - The model ID
 * @param {string} requestId - The request ID
 * @param {Object} meta - Metadata including ideaId, sceneContext, etc.
 */
function saveActiveGeneration(genKey, modelId, requestId, meta = {}) {
  try {
    // Use 'video-' prefix to identify video jobs in JobManager
    const jobId = `video-${genKey}`;

    // Register with JobManager (auto-persists to localStorage)
    JobManager.register(jobId, {
      modelId,
      requestId,
      ...meta  // includes ideaId, sceneContext, prompt, etc.
    }, {
      // Handlers are optional - we handle completion in startPolling
    });

    log.debug(` Persisted active generation for ${genKey} via JobManager`);
  } catch (e) {
    log.warn(' Could not persist active generation:', e);
  }
}

/**
 * Remove completed/failed generation from localStorage via JobManager
 * @param {string} genKey - Composite generation key
 */
function clearActiveGeneration(genKey) {
  try {
    const jobId = `video-${genKey}`;

    // Check if job exists in memory before trying to cancel it
    if (JobManager.isActive(jobId)) {
      // Use cancel() which removes from storage without triggering handlers
      JobManager.cancel(jobId);
    } else {
      // Job may be in localStorage but not in memory (after resume)
      // Manually clean up localStorage
      const STORAGE_KEY = 'cd_active_jobs';
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        try {
          const jobs = JSON.parse(raw);
          if (jobs[jobId]) {
            delete jobs[jobId];
            if (Object.keys(jobs).length > 0) {
              localStorage.setItem(STORAGE_KEY, JSON.stringify(jobs));
            } else {
              localStorage.removeItem(STORAGE_KEY);
            }
            log.debug(` Removed ${jobId} from localStorage (not in memory)`);
          }
        } catch (parseErr) {
          // Ignore parse errors
        }
      }
    }

    log.debug(` Cleared active generation for ${genKey} via JobManager`);
  } catch (e) {
    log.warn(' Could not clear active generation:', e);
  }
}

/**
 * Migrate any existing generations from old localStorage format to JobManager
 * REFACTOR-07: One-time migration from legacy storage
 */
function migrateLegacyGenerations() {
  try {
    const stored = localStorage.getItem(LEGACY_GENERATIONS_KEY);
    if (!stored) return;

    const legacyGenerations = JSON.parse(stored);
    const count = Object.keys(legacyGenerations).length;

    if (count === 0) {
      localStorage.removeItem(LEGACY_GENERATIONS_KEY);
      return;
    }

    log.debug(` Migrating ${count} legacy generation(s) to JobManager...`);

    for (const [genKey, gen] of Object.entries(legacyGenerations)) {
      const jobId = `video-${genKey}`;

      // Skip if already in JobManager
      if (JobManager.isActive(jobId)) continue;

      // Register with JobManager (preserving original startTime)
      const job = JobManager.register(jobId, {
        modelId: gen.modelId,
        requestId: gen.requestId,
        ...gen.meta
      }, {});

      // Manually update startTime to preserve original
      const jobData = JobManager.get(jobId);
      if (jobData) {
        jobData.startTime = gen.startTime;
      }
    }

    // Remove legacy storage after migration
    localStorage.removeItem(LEGACY_GENERATIONS_KEY);
    log.debug(` Legacy migration complete, removed ${LEGACY_GENERATIONS_KEY}`);
  } catch (e) {
    log.warn(' Legacy migration failed:', e);
  }
}

/**
 * Load and resume any active generations from JobManager
 * Called on init to recover videos after page refresh
 * REFACTOR-07: Now uses JobManager.resume() for persistence
 * Task 102: Handles composite keys and restores UI placeholders
 */
async function resumeActiveGenerations() {
  try {
    // First migrate any legacy generations to JobManager
    migrateLegacyGenerations();

    // Use JobManager.resume() which handles timeout automatically (10 min)
    await JobManager.resume(async (jobId, savedJob) => {
      // Only handle video generation jobs (prefixed with 'video-')
      if (!jobId.startsWith('video-')) return;

      // Extract genKey by removing 'video-' prefix
      const genKey = jobId.replace('video-', '');

      // Skip if already polling
      if (pollingIntervals[genKey]) {
        log.debug(` Already polling ${genKey}, skipping resume`);
        return;
      }

      // Extract data from saved job
      const { modelId, requestId, ideaId: savedIdeaId, sceneContext } = savedJob.data;
      const ideaId = savedIdeaId || extractIdeaIdFromKey(genKey);

      log.debug(` Resuming polling for ${genKey} (model: ${modelId})`);

      // Restore videoGenerations state with explicit ideaId
      videoGenerations[genKey] = {
        status: 'IN_PROGRESS',
        modelId,
        requestId,
        ideaId,
        sceneContext,
        ...savedJob.data
      };

      // ═══════════════════════════════════════════════════════════════════
      // CRITICAL FIX: Restore UI placeholders after page refresh
      // The DOM was reset - we must re-show the generating spinner
      // ═══════════════════════════════════════════════════════════════════
      if (sceneContext?.type === 'scene' && sceneContext?.sceneSeq != null) {
        // Scene video - restore scene card placeholder
        showSceneVideoPlaceholder(sceneContext.sceneSeq);
        log.debug(` Restored scene ${sceneContext.sceneSeq} placeholder after page refresh`);
      } else {
        // Idea-level video - restore main card placeholder
        showVideoPlaceholder(ideaId);
        log.debug(` Restored idea ${ideaId} placeholder after page refresh`);
      }

      // Also restore canvas placeholder if canvas is open for this idea
      if (window.IdeaCanvas?.isOpen()) {
        const canvasIdea = window.IdeaCanvas.getCurrentIdea();
        if (canvasIdea && String(canvasIdea.id) === String(ideaId)) {
          showCanvasVideoPlaceholder();
        }
      }

      // Resume polling with composite key
      startPolling(genKey, modelId, requestId);
    });
  } catch (e) {
    log.warn(' Could not resume active generations:', e);
  }
}

// ============================================================================
// PUBLIC API (Getters)
// ============================================================================

/**
 * Get video URL for an idea from any source (Cache, State, or Object)
 * Single Source of Truth for video URLs
 */
export function getIdeaVideoUrl(ideaId) {
  const id = String(ideaId);

  // 1. Check local cache (fastest - set immediately after generation)
  if (GeneratorState.preloadedVideos && GeneratorState.preloadedVideos[id]) {
    return GeneratorState.preloadedVideos[id];
  }

  // 2. Check active generations
  if (videoGenerations[id]?.videoUrl) {
    return videoGenerations[id].videoUrl;
  }

  // 3. Check idea object in state
  const idea = findIdea(id);
  if (idea) {
    return idea.videoUrl || idea.video_url || null;
  }

  return null;
}

/**
 * Check if video is currently generating for a specific idea (and optionally scene)
 * Task 102: Now accepts optional scene context
 * @param {string} ideaId - The idea ID
 * @param {Object|null} sceneContext - Optional scene context { type: 'scene', sceneSeq }
 * @returns {boolean}
 */
export function isVideoGenerating(ideaId, sceneContext = null) {
  const genKey = getGenerationKey(ideaId, sceneContext);
  const status = videoGenerations[genKey]?.status;
  return status === 'IN_QUEUE' || status === 'IN_PROGRESS';
}

/**
 * Check if ANY video is generating for an idea (any scene or idea-level)
 * Task 102: Helper to check all generations for an idea
 * @param {string} ideaId - The idea ID
 * @returns {boolean}
 */
export function isAnyVideoGeneratingForIdea(ideaId) {
  const id = String(ideaId);
  for (const [genKey, state] of Object.entries(videoGenerations)) {
    // Match both exact ideaId and any scene variant (ideaId_scene_N)
    if (genKey === id || genKey.startsWith(`${id}_scene_`)) {
      if (state.status === 'IN_QUEUE' || state.status === 'IN_PROGRESS') {
        return true;
      }
    }
  }
  return false;
}

/**
 * Restore video placeholders for all active generations
 * Call this after grid re-renders (e.g., after filtering) to restore placeholders
 * @returns {number} Number of placeholders restored
 */
export function restoreActiveVideoPlaceholders() {
  let restored = 0;
  for (const [genKey, state] of Object.entries(videoGenerations)) {
    if (state.status !== 'IN_QUEUE' && state.status !== 'IN_PROGRESS') continue;

    const ideaId = state.ideaId || extractIdeaIdFromKey(genKey);
    const sceneContext = state.sceneContext;

    if (sceneContext?.type === 'scene' && sceneContext?.sceneSeq != null) {
      // Scene video placeholder
      showSceneVideoPlaceholder(sceneContext.sceneSeq);
      restored++;
    } else if (ideaId) {
      // Idea-level video placeholder
      showVideoPlaceholder(ideaId);
      restored++;
    }
  }

  if (restored > 0) {
    log.debug(` Restored ${restored} video placeholder(s) after re-render`);
  }
  return restored;
}

// ============================================================================
// VIDEO PREVIEW MODAL
// ============================================================================

export function initVideoPreviewModal() {
  const modal = document.getElementById('video-preview-modal');
  if (!modal) return;

  document.getElementById('video-preview-close')?.addEventListener('click', closeVideoPreview);
  modal.addEventListener('click', (e) => {
    if (e.target === modal) closeVideoPreview();
  });

  document.getElementById('video-preview-download')?.addEventListener('click', () => {
    const video = document.getElementById('video-preview-player');
    if (video?.src) downloadVideo(video.src);
  });

  onDocument('keydown', (e) => {
    if (e.key === 'Escape' && modal.classList.contains('active')) {
      closeVideoPreview();
    }
  }, 'video-preview');
}

export function openVideoPreview(videoUrl) {
  const modal = document.getElementById('video-preview-modal');
  const video = document.getElementById('video-preview-player');
  
  if (!modal || !video) return;

  video.src = videoUrl;
  modal.classList.add('active');
  document.body.style.overflow = 'hidden';
  video.play().catch(e => log.debug(' Auto-play blocked:', e));
}

function closeVideoPreview() {
  const modal = document.getElementById('video-preview-modal');
  const video = document.getElementById('video-preview-player');

  if (modal) modal.classList.remove('active');
  if (video) {
    video.pause();
    video.src = '';
  }
  document.body.style.overflow = '';
  cleanupScope('video-preview');
}

/**
 * Download video using centralized utility with R2 proxy support
 */
async function downloadVideo(url) {
  const filename = `creative-detonator-video-${Date.now()}.mp4`;
  await downloadVideoCore(url, filename);
}

// ============================================================================
// MODAL & UI
// ============================================================================

/**
 * Open video generation modal
 * @param {string} ideaId - The idea ID
 * @param {Object} context - Optional context for scene-specific generation
 */
export function openVideoGenModal(ideaId, context = null) {
  currentSceneContext = context;

  // Scene-specific mode
  if (context?.type === 'scene') {
    openSceneVideoModal(ideaId, context);
    return;
  }

  // Original idea-level mode
  const idea = findIdea(ideaId);
  const imageUrl = getIdeaImageUrl(ideaId);

  if (!idea) {
    log.error(' Idea not found:', ideaId);
    return;
  }

  if (!imageUrl) {
    showError('Generate an image first before making a video.', { context: 'opening video generator' });
    return;
  }

  currentIdeaId = ideaId;
  currentImageUrl = imageUrl;
  currentEndImageUrl = imageUrl; // Default to loop

  // Populate Modal
  const modal = document.getElementById('video-gen-modal');
  const sourceImg = document.getElementById('video-source-image');
  const titleEl = document.getElementById('video-gen-idea-title');
  const promptInput = document.getElementById('video-gen-prompt');

  if (sourceImg) sourceImg.src = imageUrl;
  if (titleEl) titleEl.textContent = idea.title || 'Untitled';

  // Build Prompt - Enhanced format for cinematic short films
  const visual = idea.visual || idea.text || '';
  const headline = idea.headline && idea.headline !== '[Visual Only]' ? idea.headline : '';

  const prompt = `Your goal is to turn this image into a short film, presenting it as good as possible with proper voice over, music and sound effects, so the scene works good in the new format. Visual: ${visual}. ${headline ? `End with the super: "${headline}"` : ''} High production value, 4K.`;

  if (promptInput) promptInput.value = prompt;

  // Init Selector if config loaded
  initModelSelector();
  updateUIForModel(currentModelId);

  // Update context bar with brand/product info
  const updateContext = () => {
    const brief = {
      ...(idea.meta?.brief || {}),
      ...(GeneratorState.brief || {}),
      productData: GeneratorState.brief?.productData || idea.meta?.brief?.productData || GeneratorState.sessionProduct
    };
    const contextData = {
      brand: GeneratorState.currentBrand || GeneratorState.sessionBrand || idea.meta?.brand,
      brief,
      selectedProduct: GeneratorState.selectedProduct || GeneratorState.sessionProduct || brief.productData
    };
    updateContextBar('video-gen', contextData);
  };

  updateContext();
  setupContextToggle('video-gen', false);
  setTimeout(updateContext, 150); // Catch async context loading

  modal.classList.add('active');
  document.body.style.overflow = 'hidden';
}

/**
 * Open modal for scene-specific video generation
 */
function openSceneVideoModal(ideaId, context) {
  currentIdeaId = ideaId;
  currentImageUrl = context.startFrameUrl;
  currentEndImageUrl = context.endFrameUrl || context.startFrameUrl;

  const modal = document.getElementById('video-gen-modal');
  const sourceImg = document.getElementById('video-source-image');
  const endImg = document.getElementById('video-end-image');
  const titleEl = document.getElementById('video-gen-idea-title');
  const promptInput = document.getElementById('video-gen-prompt');
  const endContainer = document.getElementById('video-end-image-container');
  const startLabel = document.getElementById('video-start-label');

  // Update images
  if (sourceImg) sourceImg.src = context.startFrameUrl;
  if (endImg && context.endFrameUrl) {
    endImg.src = context.endFrameUrl;
  }

  // Show/hide end frame based on whether we have both frames
  if (endContainer) {
    endContainer.style.display = context.endFrameUrl ? 'block' : 'none';
  }
  if (startLabel) {
    startLabel.style.display = context.endFrameUrl ? 'block' : 'none';
  }

  // Update title
  if (titleEl) titleEl.textContent = `Scene ${context.sceneSeq}`;

  // Build scene-specific prompt
  const prompt = buildSceneVideoPrompt(context);
  if (promptInput) promptInput.value = prompt;

  // Auto-select Start-End model if we have both frames
  if (configCache?.models && context.endFrameUrl) {
    const startEndModel = configCache.models.find(m =>
      m.features?.supportsEndFrame && m.imageMode === 'startEnd'
    );
    if (startEndModel) {
      currentModelId = startEndModel.id;
    }
  }

  // Init selector and show modal
  initModelSelector();
  updateUIForModel(currentModelId);

  // Update context bar with brand/product info
  const idea = findIdea(ideaId);
  const updateContext = () => {
    const brief = {
      ...(idea?.meta?.brief || {}),
      ...(GeneratorState.brief || {}),
      productData: GeneratorState.brief?.productData || idea?.meta?.brief?.productData || GeneratorState.sessionProduct
    };
    const contextData = {
      brand: GeneratorState.currentBrand || GeneratorState.sessionBrand || idea?.meta?.brand,
      brief,
      selectedProduct: GeneratorState.selectedProduct || GeneratorState.sessionProduct || brief.productData
    };
    updateContextBar('video-gen', contextData);
  };

  updateContext();
  setupContextToggle('video-gen', false);
  setTimeout(updateContext, 150); // Catch async context loading

  modal.classList.add('active');
  document.body.style.overflow = 'hidden';
}

/**
 * Build video prompt from scene context
 */
function buildSceneVideoPrompt(context) {
  const visual = context.visual || '';
  const camera = context.camera || '';
  const audio = context.audio || '';

  let prompt = `Cinematic scene. ${visual}`;
  if (camera) prompt += ` Camera: ${camera}.`;
  if (audio) prompt += ` Audio: ${audio}.`;
  prompt += ' High production value, smooth motion, professional lighting.';

  return prompt;
}

/**
 * Initialize model selector dropdown
 */
function initModelSelector() {
  if (configCache && configCache.models) {
    const selector = document.getElementById('video-model-selector');
    if (selector) {
      selector.innerHTML = configCache.models.map(m =>
        `<option value="${m.id}">${m.name}</option>`
      ).join('');
      selector.value = currentModelId;
    }
  }
}

export function closeVideoGenModal() {
  const modal = document.getElementById('video-gen-modal');
  if (modal) modal.classList.remove('active');
  document.body.style.overflow = '';
  currentIdeaId = null;
  currentSceneContext = null;
  cleanupScope('video-modal');
}

function updateUIForModel(modelId) {
  const model = configCache?.models?.find(m => m.id === modelId);
  if (!model) return;
  currentModelId = modelId;

  log.debug(' Updating UI for model:', model.name);

  // 1. Duration UI
  const durContainer = document.getElementById('video-duration-container');
  if (durContainer) {
    if (model.durations && model.durations.length === 1) {
      durContainer.innerHTML = `<span class="setting-badge is-fixed">${model.durations[0]}s Fixed</span>`;
      currentDuration = model.durations[0];
    } else if (model.durations && model.durations.length > 1) {
      durContainer.innerHTML = model.durations.map(d => 
        `<button class="toggle-btn ${d === currentDuration ? 'active' : ''}" data-val="${d}">${d}s</button>`
      ).join('');
      durContainer.querySelectorAll('.toggle-btn').forEach(btn => {
        btn.onclick = () => {
          durContainer.querySelectorAll('.toggle-btn').forEach(b => b.classList.remove('active'));
          btn.classList.add('active');
          currentDuration = btn.dataset.val;
          updateCostDisplay();
        };
      });
    }
  }

  // 2. Resolution UI
  updateResolutionUI(model);

  // 3. Aspect Ratio UI
  updateAspectUI(model);

  // 4. Start/End Frame UI
  updateImageModeUI(model);
  
  // 5. Camera Fixed (Seedance only)
  const camRow = document.getElementById('video-camera-fixed-row');
  if (camRow) {
    camRow.style.display = model.features?.supportsCameraFixed ? 'flex' : 'none';
  }

  // Reset settings to defaults
  currentDuration = model.defaults?.duration || model.durations?.[0] || '8';
  currentResolution = model.defaults?.resolution || model.resolutions?.[0] || '720p';
  currentAspectRatio = model.defaults?.aspectRatio || model.aspectRatios?.[0] || '16:9';
  currentAudioEnabled = model.defaults?.generateAudio !== false;
  currentCameraFixed = model.defaults?.cameraFixed || false;

  resetTogglesToDefaults(model);
  updateCostDisplay();
}

function updateResolutionUI(model) {
  const toggle = document.getElementById('video-resolution-toggle');
  if (!toggle) return;

  toggle.innerHTML = '';
  const resolutions = model.resolutions || ['720p', '1080p'];

  resolutions.forEach(res => {
    const btn = document.createElement('button');
    btn.className = 'toggle-btn' + (res === model.defaults?.resolution ? ' active' : '');
    btn.dataset.value = res;
    btn.textContent = res;
    btn.addEventListener('click', () => {
      toggle.querySelectorAll('.toggle-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      currentResolution = res;
      updateCostDisplay();
    });
    toggle.appendChild(btn);
  });

  currentResolution = model.defaults?.resolution || resolutions[0];
}

function updateAspectUI(model) {
  const aspectRow = document.getElementById('video-aspect-row');
  const aspectToggle = document.getElementById('video-aspect-toggle');
  
  if (!aspectRow || !aspectToggle) return;

  if (model.aspectRatios && model.aspectRatios.length > 0) {
    aspectRow.style.display = 'flex';
    aspectToggle.innerHTML = '';

    const defaultAspect = model.defaults?.aspectRatio || '16:9';

    model.aspectRatios.forEach(ratio => {
      const btn = document.createElement('button');
      btn.className = 'toggle-btn' + (ratio === defaultAspect ? ' active' : '');
      btn.dataset.value = ratio;
      btn.textContent = ratio === 'auto' ? 'Native' : ratio;
      
      btn.addEventListener('click', () => {
        aspectToggle.querySelectorAll('.toggle-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        currentAspectRatio = ratio;
        updateCostDisplay();
      });
      
      aspectToggle.appendChild(btn);
    });

    currentAspectRatio = defaultAspect;
  } else {
    aspectRow.style.display = 'none';
    currentAspectRatio = null;
  }
}

function updateImageModeUI(model) {
  const endContainer = document.getElementById('video-end-image-container');
  const startLabel = document.getElementById('video-start-label');
  const endImg = document.getElementById('video-end-image');
  
  if (endContainer) {
    if (model.imageMode === 'startEnd' && model.features?.supportsEndFrame) {
      endContainer.style.display = 'block';
      if (startLabel) startLabel.style.display = 'block';
      if (endImg) endImg.src = currentEndImageUrl || currentImageUrl;
    } else {
      endContainer.style.display = 'none';
      if (startLabel) startLabel.style.display = 'none';
    }
  }
}

function resetTogglesToDefaults(model) {
  // Resolution
  const resToggle = document.getElementById('video-resolution-toggle');
  if (resToggle) {
    resToggle.querySelectorAll('.toggle-btn').forEach(btn => {
      btn.classList.toggle('active', btn.dataset.value === currentResolution);
    });
  }

  // Aspect Ratio
  const aspectToggle = document.getElementById('video-aspect-toggle');
  if (aspectToggle) {
    aspectToggle.querySelectorAll('.toggle-btn').forEach(btn => {
      btn.classList.toggle('active', btn.dataset.value === currentAspectRatio);
    });
  }

  // Audio
  const audioToggle = document.getElementById('video-audio-toggle');
  if (audioToggle) {
    audioToggle.querySelectorAll('.toggle-btn').forEach(btn => {
      btn.classList.toggle('active', btn.dataset.value === String(currentAudioEnabled));
    });
  }

  // Camera Fixed
  const cameraToggle = document.getElementById('video-camera-fixed-toggle');
  if (cameraToggle) {
    cameraToggle.querySelectorAll('.toggle-btn').forEach(btn => {
      btn.classList.toggle('active', btn.dataset.value === String(currentCameraFixed));
    });
  }
}

/**
 * Calculate video generation credit cost using centralized pricing
 * Uses getVideoCost() from frontend/js/core/pricing.js (Single Source of Truth)
 * @returns {number} Credit cost for current video configuration
 */
function calculateVideoCreditCost() {
  const model = configCache?.models?.find(m => m.id === currentModelId);

  // Determine provider from model ID
  let provider = 'veo';
  if (model?.id?.toLowerCase().includes('bytedance') ||
      model?.id?.toLowerCase().includes('seedance')) {
    provider = 'bytedance';
  }

  // Use centralized pricing calculation
  return getVideoCost({
    provider,
    duration: parseInt(currentDuration),
    generateAudio: currentAudioEnabled,
    resolution: currentResolution
  });
}

/**
 * Calculate USD cost for display (derived from credits)
 * 1 credit = $0.01
 */
function calculateVideoUSDCost() {
  const credits = calculateVideoCreditCost();
  return credits / 100;
}

function updateCostDisplay() {
  const costEl = document.getElementById('video-cost-value');
  const creditsEl = document.getElementById('video-cost-credits');
  const btnCreditsValue = document.querySelector('#video-gen-btn-credits .btn-credits-value');
  if (!costEl) return;

  // Use centralized credit calculation
  const credits = calculateVideoCreditCost();
  const usdCost = credits / 100;

  costEl.textContent = `$${usdCost.toFixed(2)}`;

  if (creditsEl) {
    creditsEl.textContent = `(${credits} C)`;
  }

  // FIX UI-5: Update button credit badge
  if (btnCreditsValue) {
    btnCreditsValue.textContent = credits;
  }
}

// ============================================================================
// GENERATION LOGIC
// ============================================================================

async function submitVideoGeneration() {
  const btn = document.getElementById('video-gen-submit');
  const btnText = btn?.querySelector('.btn-text');
  const btnLoad = btn?.querySelector('.btn-loading');

  if (btnText) btnText.style.display = 'none';
  if (btnLoad) btnLoad.style.display = 'inline';
  if (btn) btn.disabled = true;

  // Save scene context before modal closes
  const sceneContext = currentSceneContext;

  try {
    const model = configCache?.models?.find(m => m.id === currentModelId);
    const payload = {
      ideaId: currentIdeaId,
      modelId: currentModelId,
      prompt: document.getElementById('video-gen-prompt')?.value || '',
      imageUrl: currentImageUrl,
      duration: currentDuration,
      resolution: currentResolution,
      generateAudio: currentAudioEnabled
    };

    // Add aspect ratio if model supports it
    if (currentAspectRatio && model?.aspectRatios && model.aspectRatios.length > 0) {
      payload.aspectRatio = currentAspectRatio;
    }

    // Add end image if model supports it
    if (model?.features?.supportsEndFrame) {
      payload.endImageUrl = currentEndImageUrl || currentImageUrl;
    }

    // Add camera fixed if model supports it
    if (model?.features?.supportsCameraFixed) {
      payload.cameraFixed = currentCameraFixed;
    }

    // Add scene context if generating for a scene
    if (sceneContext?.type === 'scene') {
      payload.sceneContext = {
        sceneSeq: sceneContext.sceneSeq,
        type: 'scene'
      };
    }

    log.debug(' Submitting:', payload);

    // Get auth token
    const token = await getSessionToken();
    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const res = await fetch(getApiUrl(ENDPOINTS.submit), {
      method: 'POST',
      headers,
      body: JSON.stringify(payload)
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.error || `HTTP ${res.status}`);
    }

    if (data.success) {
      // Save ID before closing modal
      const ideaId = currentIdeaId;
      // Task 102: Generate composite key for concurrent generation tracking
      const genKey = getGenerationKey(ideaId, sceneContext);

      // CREDIT-FIX: Use centralized cost calculation for consistency with UI display
      const videoCreditCost = calculateVideoCreditCost();
      animateCoinSpend(videoCreditCost);

      closeVideoGenModal();

      // Show placeholder in scene card if scene video, otherwise idea card
      if (sceneContext?.type === 'scene') {
        showSceneVideoPlaceholder(sceneContext.sceneSeq);
      } else {
        showVideoPlaceholder(ideaId);

        // Also show placeholder in canvas if open for this idea
        if (window.IdeaCanvas?.isOpen()) {
          const canvasIdea = window.IdeaCanvas.getCurrentIdea();
          if (canvasIdea && String(canvasIdea.id) === String(ideaId)) {
            showCanvasVideoPlaceholder();
          }
        }
      }

      // Task 102: Use composite key for state storage
      videoGenerations[genKey] = {
        status: 'IN_QUEUE',
        requestId: data.requestId,
        modelId: data.modelId,
        ideaId: ideaId,  // Store ideaId explicitly for DB operations
        prompt: payload.prompt,
        duration: currentDuration,
        resolution: currentResolution,
        aspectRatio: currentAspectRatio,
        sceneContext: sceneContext // Store scene context for completion handler
      };

      // Persist to localStorage so we can resume after page refresh
      // Task 102: Use composite key and include ideaId in meta
      saveActiveGeneration(genKey, data.modelId, data.requestId, {
        ideaId: ideaId,  // Include ideaId in meta for resume logic
        prompt: payload.prompt,
        duration: currentDuration,
        resolution: currentResolution,
        aspectRatio: currentAspectRatio,
        imageUrl: currentImageUrl,
        sceneContext: sceneContext
      });

      // Task 102: Pass composite key to startPolling
      startPolling(genKey, data.modelId, data.requestId);

      // REFACTOR-03: Emit video generation started event
      emit(VIDEO_EVENTS.GENERATION_STARTED, {
        ideaId,
        requestId: data.requestId,
        modelId: data.modelId,
        sceneContext
      });
    } else {
      throw new Error(data.error || 'Video generation failed');
    }
  } catch (e) {
    log.error(' Submission failed:', e);
    showError(e, { context: 'submitting video generation' });
  } finally {
    if (btnText) btnText.style.display = 'inline';
    if (btnLoad) btnLoad.style.display = 'none';
    if (btn) btn.disabled = false;
  }
}

/**
 * Show video generating placeholder in scene card
 * CRITICAL FIX: Must handle both empty state (.video-gen-section) AND existing video state (.scene-video-player)
 */
function showSceneVideoPlaceholder(sceneSeq) {
  // 1. Find the specific scene card
  const card = document.querySelector(`.scene-card-screenplay[data-scene="${sceneSeq}"]`);
  if (!card) return;

  // 2. Find the target container to replace.
  // Look for .video-gen-section (Idle state) OR .scene-video-player (Existing video state)
  const targetEl = card.querySelector('.video-gen-section') || card.querySelector('.scene-video-player');

  if (targetEl) {
    // 3. Define the placeholder HTML structure
    // Wrap in 'video-gen-section' to match the 'Generating' state structure in ui/scenes.js
    const placeholderHTML = `
      <div class="video-gen-section">
        <div class="scene-video-generating">
          <div class="pixel-loader-sm"></div>
          <span class="generating-text">RENDERING VIDEO...</span>
          <span class="generating-subtext">1-2 minutes</span>
        </div>
      </div>`;

    // 4. Replace the entire element (outerHTML) so classes update correctly
    targetEl.outerHTML = placeholderHTML;

    console.log(`[VideoGen] UI updated to placeholder for scene ${sceneSeq}`);
  } else {
    console.warn(`[VideoGen] Could not find video container to replace for scene ${sceneSeq}`);
  }
}

/**
 * Start polling for video generation status
 * Task 102: Refactored to use composite keys for concurrent generation tracking
 * @param {string} genKey - Composite generation key (ideaId or ideaId_scene_N)
 * @param {string} modelId - The model ID
 * @param {string} requestId - The request ID
 */
function startPolling(genKey, modelId, requestId) {
  // Clear any existing polling for this key
  if (pollingIntervals[genKey]) clearInterval(pollingIntervals[genKey]);

  // Extract ideaId from state (preferred) or parse from key
  const state = videoGenerations[genKey];
  const ideaId = state?.ideaId || extractIdeaIdFromKey(genKey);
  const storedSceneContext = state?.sceneContext;

  const startTime = Date.now();
  const TIMEOUT = 300000; // 5 mins

  pollingIntervals[genKey] = setInterval(async () => {
    if (Date.now() - startTime > TIMEOUT) {
      clearInterval(pollingIntervals[genKey]);
      delete pollingIntervals[genKey];
      clearActiveGeneration(genKey); // Clean up localStorage with composite key
      cleanupVideoGenState(ideaId); // Clean up UI state
      // Task 102: Pass scene seq for scene-specific error display
      const sceneSeq = storedSceneContext?.sceneSeq;
      showVideoError(ideaId, 'Timed out', sceneSeq);
      return;
    }

    try {
      const res = await fetch(getApiUrl(ENDPOINTS.status(modelId, requestId)));
      const data = await res.json();

      if (data.status === 'COMPLETED') {
        clearInterval(pollingIntervals[genKey]);
        delete pollingIntervals[genKey];

        // Fetch Result
        const resultRes = await fetch(getApiUrl(ENDPOINTS.result(modelId, requestId)));
        const resultData = await resultRes.json();

        log.debug(' Result data:', resultData);

        // Robust extraction of video URL
        const videoUrl = extractVideoUrl(resultData);

        log.debug(' Extracted videoUrl:', videoUrl);

        if (videoUrl) {
          log.debug(' Video URL found, updating state and UI for genKey:', genKey, 'ideaId:', ideaId);

          // Get stored scene context from state
          const sceneContext = videoGenerations[genKey]?.sceneContext;

          // 1. Update State Cache immediately
          if (!GeneratorState.preloadedVideos) GeneratorState.preloadedVideos = {};
          GeneratorState.preloadedVideos[String(ideaId)] = videoUrl;

          // Task 102: Track latest media timestamp for display priority
          if (!GeneratorState.latestMediaTimestamp) GeneratorState.latestMediaTimestamp = {};
          GeneratorState.latestMediaTimestamp[String(ideaId)] = {
            type: 'video',
            timestamp: Date.now()
          };

          // Persist immediately so timestamp survives page refresh
          persistSessionState();

          // 2. Update local tracking with composite key
          videoGenerations[genKey] = {
            ...videoGenerations[genKey],
            status: 'COMPLETED',
            videoUrl
          };

          // 3. Handle scene video vs idea video
          if (sceneContext?.type === 'scene') {
            log.debug(' Scene video complete, dispatching event for scene:', sceneContext.sceneSeq);

            // Dispatch scene-specific event for storyboard UI
            document.dispatchEvent(new CustomEvent('storyboard:sceneVideoComplete', {
              detail: {
                ideaId,
                sceneSeq: sceneContext.sceneSeq,
                videoUrl,
                meta: {
                  provider: videoGenerations[genKey].modelId?.includes('bytedance') ? 'bytedance' : 'veo',
                  modelId: videoGenerations[genKey].modelId,
                  prompt: videoGenerations[genKey].prompt,
                  duration: videoGenerations[genKey].duration,
                  resolution: videoGenerations[genKey].resolution
                }
              }
            }));

            // Save to scene-specific endpoint (uses ideaId for DB)
            await saveSceneVideoToDatabase(ideaId, sceneContext.sceneSeq, videoUrl, videoGenerations[genKey]);
            clearActiveGeneration(genKey); // Clean up localStorage with composite key
            cleanupVideoGenState(ideaId); // Clean up UI state

            // Refresh credits from server (animation already shown on submit)
            refreshCreditsFromServer();
          } else {
            // Original idea-level video flow
            showGeneratedVideo(ideaId, videoUrl);

            // 4. Refresh canvas if open for this idea (updates left-side preview)
            if (window.IdeaCanvas?.isOpen()) {
              const canvasIdea = window.IdeaCanvas.getCurrentIdea();
              if (canvasIdea && String(canvasIdea.id) === String(ideaId)) {
                log.debug(' Canvas open for this idea, updating with video...');
                // Update the idea's video URL so render() picks it up
                canvasIdea.videoUrl = videoUrl;
                canvasIdea.video_url = videoUrl;
                // Use updateIdea() to ensure currentIdea is updated and re-rendered
                window.IdeaCanvas.updateIdea(canvasIdea);
              }
            }

            // Clean up any lingering UI state
            cleanupVideoGenState(ideaId);

            // 5. Save to DB (Persistent) with retry logic
            log.debug(' Starting DB save for idea:', ideaId);
            await saveVideoToDatabase(ideaId, videoUrl, videoGenerations[genKey]);
            log.debug(' DB save complete for idea:', ideaId);
            clearActiveGeneration(genKey); // Clean up localStorage with composite key

            // Refresh credits from server (animation already shown on submit)
            refreshCreditsFromServer();
          }
        } else {
          log.error(' No video URL extracted from response');
          clearActiveGeneration(genKey); // Clean up localStorage with composite key
          cleanupVideoGenState(ideaId); // Clean up UI state
          const sceneSeq = videoGenerations[genKey]?.sceneContext?.sceneSeq;
          showVideoError(ideaId, 'No video URL in response', sceneSeq);
        }

      } else if (data.status === 'FAILED') {
        clearInterval(pollingIntervals[genKey]);
        delete pollingIntervals[genKey];
        clearActiveGeneration(genKey); // Clean up localStorage with composite key
        cleanupVideoGenState(ideaId); // Clean up UI state
        const sceneSeq = videoGenerations[genKey]?.sceneContext?.sceneSeq;
        showVideoError(ideaId, data.error || 'Generation failed', sceneSeq);
      }
    } catch (e) {
      log.error(' Poll error', e);
    }
  }, 4000);
}

function extractVideoUrl(data) {
  log.debug(' Extracting video URL from:', JSON.stringify(data, null, 2).substring(0, 500));

  // Handle various Fal.ai response shapes
  // Shape 1: { success: true, data: { video: { url: '...' } } }
  if (data.data?.video?.url) {
    log.debug(' Found URL at data.data.video.url');
    return data.data.video.url;
  }

  // Shape 2: { video: { url: '...' } }
  if (data.video?.url) {
    log.debug(' Found URL at data.video.url');
    return data.video.url;
  }

  // Shape 3: { success: true, data: { url: '...' } }
  if (data.data?.url) {
    log.debug(' Found URL at data.data.url');
    return data.data.url;
  }

  // Shape 4: { success: true, data: '...' } (direct string)
  if (typeof data.data === 'string') {
    log.debug(' Found URL as data.data string');
    return data.data;
  }

  // Shape 5: Bytedance specific - check for video_url
  if (data.data?.video_url) {
    log.debug(' Found URL at data.data.video_url');
    return data.data.video_url;
  }

  // Shape 6: Direct url on response
  if (data.url) {
    log.debug(' Found URL at data.url');
    return data.url;
  }

  log.warn(' Could not extract video URL. Full response:', data);
  return null;
}

// ============================================================================
// DATABASE PERSISTENCE WITH RETRY LOGIC
// ============================================================================

/**
 * Save video to database with multi-strategy UUID resolution
 * This is the main entry point - resolves IDs and handles failures gracefully
 *
 * @param {string} ideaId - The idea ID (may be temp ID or UUID)
 * @param {string} videoUrl - The video URL
 * @param {Object} meta - Video metadata
 */
async function saveVideoToDatabase(ideaId, videoUrl, meta = {}) {
  const token = await getSessionToken();
  if (!token) {
    log.warn(' No auth token, video saved to session only');
    return;
  }

  // Resolve ID to UUID using all strategies
  const dbId = resolveToUUID(ideaId);

  if (!dbId) {
    log.warn(` Could not resolve UUID for ${ideaId}. Video saved to session only.`);

    // Queue for later sync when idea gets saved
    queueVideoForSync(ideaId, videoUrl, meta);
    return;
  }

  // Attempt to save with retry logic
  const success = await saveVideoToDatabaseInternal(dbId, ideaId, videoUrl, meta);

  if (!success) {
    // Queue for later sync
    queueVideoForSync(ideaId, videoUrl, meta);
  }
}

/**
 * Save scene video to the scene-specific endpoint
 * @param {string} ideaId - The idea ID
 * @param {number} sceneSeq - Scene sequence number
 * @param {string} videoUrl - The video URL
 * @param {Object} meta - Video metadata
 */
async function saveSceneVideoToDatabase(ideaId, sceneSeq, videoUrl, meta = {}) {
  const token = await getSessionToken();
  if (!token) {
    log.warn(' No auth token, scene video saved to session only');
    return;
  }

  // Resolve ID to UUID
  const dbId = resolveToUUID(ideaId);
  if (!dbId) {
    log.warn(` Could not resolve UUID for ${ideaId}. Scene video saved to session only.`);
    return;
  }

  const provider = meta.modelId?.includes('bytedance') ? 'bytedance' : 'veo';

  try {
    log.debug(` Saving scene ${sceneSeq} video to database...`);

    const res = await fetch(`${API_CONFIG.baseUrl}/api/ideas/${dbId}/scene-video`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        videoUrl,
        sceneSeq,
        prompt: meta.prompt,
        duration: meta.duration,
        resolution: meta.resolution,
        aspectRatio: meta.aspectRatio,
        sourceImageUrl: meta.imageUrl,
        modelId: meta.modelId,
        brandId: GeneratorState.currentBrand?.id || null  // V5: Link scene video to brand
      })
    });

    const data = await res.json();

    if (data.success) {
      log.debug(` ✅ Scene ${sceneSeq} video saved to DB successfully`);
      // Update local timestamp for sorting in Dashboard Recent Ideas
      window.DashboardCore?.Utils?.touchIdea(ideaId);
    } else {
      log.error(` Failed to save scene video:`, data.error);
    }
  } catch (e) {
    log.error(` Scene video save error:`, e);
  }
}

/**
 * Internal database save function with retry logic
 * Called by both saveVideoToDatabase and processPendingVideoSyncs
 *
 * @param {string} dbId - The resolved database UUID
 * @param {string} originalId - The original idea ID (for UI updates)
 * @param {string} videoUrl - The video URL
 * @param {Object} meta - Video metadata
 * @returns {boolean} True if save succeeded
 */
async function saveVideoToDatabaseInternal(dbId, originalId, videoUrl, meta = {}) {
  const token = await getSessionToken();
  if (!token) {
    log.warn(' No auth token available');
    return false;
  }

  // Retry logic - try up to 3 times with exponential backoff
  let attempts = 0;
  const maxAttempts = 3;

  while (attempts < maxAttempts) {
    try {
      log.debug(` DB Save attempt ${attempts + 1}/${maxAttempts} for idea ${dbId}`);

      const res = await fetch(`${API_CONFIG.baseUrl}/api/ideas/${dbId}/video`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          videoUrl,
          modelId: meta.modelId,
          prompt: meta.prompt,
          duration: meta.duration,
          resolution: meta.resolution,
          aspectRatio: meta.aspectRatio,
          brandId: GeneratorState.currentBrand?.id || null  // V5: Link video to brand
        })
      });

      const data = await res.json();

      if (data.success) {
        log.debug(' ✅ Video saved to DB successfully');
        // No status badge needed - success is silent

        // Update idea object in GeneratorState
        updateIdeaWithVideo(originalId, videoUrl);

        // Update local timestamp for sorting in Dashboard Recent Ideas
        window.DashboardCore?.Utils?.touchIdea(originalId);

        // Refresh sidebar counts (Dashboard only)
        if (window.DashboardCore?.loadSidebarCounts) {
          log.debug(' Refreshing sidebar counts...');
          window.DashboardCore.loadSidebarCounts(true);
        }

        return true;
      } else {
        throw new Error(data.error || 'Unknown error');
      }
    } catch (e) {
      attempts++;
      log.error(` DB Save attempt ${attempts} failed:`, e.message);

      if (attempts < maxAttempts) {
        // Wait with exponential backoff: 1s, 2s, 4s
        const delay = Math.pow(2, attempts - 1) * 1000;
        log.debug(` Retrying in ${delay}ms...`);
        await new Promise(r => setTimeout(r, delay));
      }
    }
  }

  log.error(' ❌ All DB save attempts failed');
  // Don't show error badge - fail silently, video is still in session
  return false;
}

/**
 * Show visual feedback for video save status
 * @param {string} ideaId - The idea ID
 * @param {string} status - Status type: 'success', 'warning', 'error'
 * @param {string} message - Status message to display
 */
function showVideoSaveStatus(ideaId, status, message) {
  const card = document.querySelector(`.idea-card[data-id="${ideaId}"]`);
  if (!card) return;

  // Find the video container
  const videoContainer = card.querySelector('.idea-video-container') ||
                          card.querySelector('.generated-video-wrapper');
  if (!videoContainer) return;

  // Find or create status indicator
  let indicator = videoContainer.querySelector('.video-save-status');
  if (!indicator) {
    indicator = document.createElement('div');
    indicator.className = 'video-save-status';
    videoContainer.style.position = 'relative';
    videoContainer.appendChild(indicator);
  }

  // Update status
  indicator.className = `video-save-status ${status}`;
  indicator.textContent = message;
  indicator.style.display = 'block';

  // Auto-hide success messages after 3s
  if (status === 'success') {
    setTimeout(() => {
      indicator.classList.add('fade-out');
      setTimeout(() => {
        indicator.style.display = 'none';
        indicator.classList.remove('fade-out');
      }, 300);
    }, 3000);
  }
}

function updateIdeaWithVideo(ideaId, videoUrl) {
  if (!GeneratorState.generatedIdeas) return;
  
  for (const group of GeneratorState.generatedIdeas) {
    const idea = group.ideas?.find(i => String(i.id) === String(ideaId));
    if (idea) {
      idea.videoUrl = videoUrl;
      idea.video_url = videoUrl;
      log.debug(` Updated idea ${ideaId} with video URL in state`);
      return;
    }
  }
}

// ============================================================================
// UI HELPERS
// ============================================================================

/**
 * Clean up UI state after video generation completes
 * Prevents UI freezing issues by resetting any lingering state
 * Task 102: Now checks for other active generations before resetting global state
 * @param {string} ideaId - The idea ID
 */
function cleanupVideoGenState(ideaId) {
  // ═══════════════════════════════════════════════════════════════════
  // Task 102: Only reset global UI state if NO other generations are active
  // ═══════════════════════════════════════════════════════════════════
  const hasActiveGenerations = Object.values(videoGenerations).some(
    gen => gen.status === 'IN_QUEUE' || gen.status === 'IN_PROGRESS'
  );

  if (!hasActiveGenerations) {
    // Safe to reset global states - no other generations running
    // Ensure video gen modal is closed
    const genModal = document.getElementById('video-gen-modal');
    if (genModal?.classList.contains('active')) {
      genModal.classList.remove('active');
    }

    // Reset body overflow
    document.body.style.overflow = '';
    document.body.style.userSelect = '';
    document.body.style.cursor = '';

    // Clear any lingering overlays that might block interaction
    document.querySelectorAll('.modal-overlay.active').forEach(overlay => {
      if (overlay.id === 'video-gen-modal') {
        overlay.classList.remove('active');
      }
    });
  }

  // Always clean up idea-specific state
  const card = document.querySelector(`.idea-card[data-id="${ideaId}"]`);
  if (card) {
    card.classList.remove('video-generating');
  }

  log.debug(' Cleaned up UI state for', ideaId, hasActiveGenerations ? '(other generations still active)' : '(all done)');
}

/**
 * Show video generating placeholder in canvas left column
 */
function showCanvasVideoPlaceholder() {
  const canvasInner = document.querySelector('.canvas-inner');
  const leftColumn = canvasInner?.querySelector('.canvas-col-image');
  if (!leftColumn) return;

  leftColumn.innerHTML = `
    <div class="canvas-placeholder generating">
      <div class="pixel-loader"></div>
      <div class="canvas-placeholder-text">RENDERING VIDEO...</div>
      <div class="canvas-placeholder-subtext">This may take 1-2 minutes</div>
    </div>
  `;
}

function showVideoPlaceholder(ideaId) {
  const card = document.querySelector(`.idea-card[data-id="${ideaId}"]`);
  if (!card) return;
  
  let container = card.querySelector('.idea-video-container');
  if (!container) {
    container = document.createElement('div');
    container.className = 'idea-video-container';
    const imgContainer = card.querySelector('.idea-image-container');
    if (imgContainer) {
      imgContainer.after(container);
      // Hide image while video is generating
      imgContainer.style.display = 'none';
    } else {
      card.querySelector('.idea-card-header')?.after(container);
    }
  }
  
  container.innerHTML = `
    <div class="video-placeholder generating">
      <div class="pixel-loader"></div>
      <span class="placeholder-text">RENDERING VIDEO...</span>
      <span class="placeholder-subtext">This may take 1-2 minutes</span>
    </div>`;
}

function showGeneratedVideo(ideaId, url) {
  const card = document.querySelector(`.idea-card[data-id="${ideaId}"]`);
  if (!card) {
    log.debug(` showGeneratedVideo: card not found for ${ideaId}`);
    return;
  }

  // Get the image URL for the regenerate video button
  const imageUrl = getIdeaImageUrl(ideaId);

  // Build video HTML matching idea-card.js renderVideoPlayer structure
  const videoHtml = `
    <div class="idea-video-container">
      <div class="film-stripe film-stripe-top"></div>
      <div class="generated-video-wrapper">
        <video src="${url}" class="generated-video" controls preload="metadata">
          Your browser doesn't support video playback.
        </video>
        <div class="video-actions">
          ${renderHistoryButton(ideaId)}
          <button class="btn-img-action btn-video-preview" data-url="${url}" title="View Fullscreen">
            ${Icons.fullscreen16()}
          </button>
          <button class="btn-img-action btn-video-download" data-url="${url}" title="Download Video">
            ${Icons.download()}
          </button>
          ${imageUrl ? `
          <button class="btn-img-action btn-make-video" data-id="${ideaId}" data-image-url="${imageUrl}" title="Regenerate Video">
            ${Icons.makeVideo()}
          </button>
          ` : ''}
        </div>
      </div>
      <div class="film-stripe film-stripe-bottom"></div>
    </div>`;

  // Check for existing video container (from placeholder or previous video)
  let container = card.querySelector('.idea-video-container');
  if (container) {
    // Replace the placeholder content with the video player
    container.outerHTML = videoHtml;
  } else {
    // No video container - check for image container and replace it
    const imgContainer = card.querySelector('.idea-image-container');
    if (imgContainer) {
      imgContainer.outerHTML = videoHtml;
    } else {
      // Fallback: insert after the header
      const header = card.querySelector('.idea-card-header');
      if (header) {
        header.insertAdjacentHTML('afterend', videoHtml);
      }
    }
  }

  log.debug(` showGeneratedVideo: updated card UI for ${ideaId}`);

  // REFACTOR-03: Emit events for cross-module communication
  emit(VIDEO_EVENTS.GENERATION_COMPLETED, { ideaId, videoUrl: url });
  emit(IDEA_EVENTS.VIDEO_GENERATED, { ideaId, videoUrl: url });
}

/**
 * Show video generation error in the appropriate location
 * Task 102: Now accepts optional sceneSeq for scene-specific error display
 * @param {string} ideaId - The idea ID
 * @param {string} msg - Error message to display
 * @param {number|null} sceneSeq - Scene sequence number (if scene video error)
 */
function showVideoError(ideaId, msg, sceneSeq = null) {
  let container;

  if (sceneSeq != null) {
    // Scene video error - target the scene card's video section
    container = document.querySelector(
      `.scene-card-screenplay[data-scene="${sceneSeq}"] .video-gen-section`
    );
    if (container) {
      container.innerHTML = `
        <div class="scene-video-error">
          <span class="error-icon">!</span>
          <span class="error-text">FAILED: ${msg}</span>
          <button class="btn-retry-scene-video" data-idea-id="${ideaId}" data-scene="${sceneSeq}">RETRY</button>
        </div>`;
    }
  } else {
    // Idea-level video error - target the main card
    container = document.querySelector(`.idea-card[data-id="${ideaId}"] .idea-video-container`);
    if (container) {
      container.innerHTML = `
        <div class="video-placeholder error">
          <span class="error-icon">!</span>
          <span class="error-text">FAILED: ${msg}</span>
          <button class="btn-retry-video" data-id="${ideaId}">RETRY</button>
        </div>`;
    }
  }

  // REFACTOR-03: Emit video generation failed event
  emit(VIDEO_EVENTS.GENERATION_FAILED, { ideaId, error: msg, sceneSeq });
}

function findIdea(id) {
  if (!GeneratorState.generatedIdeas) return null;
  
  for (const g of GeneratorState.generatedIdeas) {
    const f = g.ideas?.find(i => String(i.id) === String(id));
    if (f) return f;
  }
  return null;
}

// ============================================================================
// EVENT HANDLERS
// ============================================================================

function initModalEvents() {
  document.getElementById('video-model-selector')?.addEventListener('change', (e) => {
    updateUIForModel(e.target.value);
  });
  
  document.getElementById('video-gen-close')?.addEventListener('click', closeVideoGenModal);
  document.getElementById('video-gen-cancel')?.addEventListener('click', closeVideoGenModal);
  document.getElementById('video-gen-submit')?.addEventListener('click', submitVideoGeneration);

  // Audio toggle
  const audioToggle = document.getElementById('video-audio-toggle');
  if (audioToggle) {
    audioToggle.addEventListener('click', (e) => {
      const btn = e.target.closest('.toggle-btn');
      if (!btn) return;
      audioToggle.querySelectorAll('.toggle-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      currentAudioEnabled = btn.dataset.value === 'true';
      updateCostDisplay();
    });
  }

  // Camera fixed toggle
  const cameraToggle = document.getElementById('video-camera-fixed-toggle');
  if (cameraToggle) {
    cameraToggle.addEventListener('click', (e) => {
      const btn = e.target.closest('.toggle-btn');
      if (!btn) return;
      cameraToggle.querySelectorAll('.toggle-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      currentCameraFixed = btn.dataset.value === 'true';
    });
  }

  // Escape key
  onDocument('keydown', (e) => {
    const modal = document.getElementById('video-gen-modal');
    if (e.key === 'Escape' && modal?.classList.contains('active')) {
      closeVideoGenModal();
    }
  }, 'video-modal');
}

function initCardEvents() {
  if (window._videoGenEventsInitialized) return;
  
  document.body.addEventListener('click', (e) => {
    // Make Video button
    const btn = e.target.closest('.btn-make-video');
    if (btn) {
      // Skip if Dashboard already handled this (Traffic Cop pattern)
      // Dashboard uses stopImmediatePropagation + preventDefault
      if (e.defaultPrevented) {
        log.debug(' Skipping - event already handled by Dashboard');
        return;
      }
      e.stopPropagation();
      openVideoGenModal(btn.dataset.id);
      return;
    }

    // Retry button
    const retryBtn = e.target.closest('.btn-retry-video');
    if (retryBtn) {
      e.stopPropagation();
      openVideoGenModal(retryBtn.dataset.id);
      return;
    }

    // Video preview button
    const previewBtn = e.target.closest('.btn-video-preview');
    if (previewBtn) {
      e.stopPropagation();
      const url = previewBtn.dataset.url;
      if (url) openVideoPreview(url);
      return;
    }

    // Video download button
    const downloadBtn = e.target.closest('.btn-video-download');
    if (downloadBtn) {
      e.stopPropagation();
      const url = downloadBtn.dataset.url;
      if (url) downloadVideo(url);
      return;
    }

    // Click on video itself
    const videoEl = e.target.closest('.generated-video');
    if (videoEl && e.target.tagName === 'VIDEO') {
      const rect = videoEl.getBoundingClientRect();
      const controlsHeight = 40;
      if (e.clientY < rect.bottom - controlsHeight) {
        e.preventDefault();
        e.stopPropagation();
        videoEl.pause();
        openVideoPreview(videoEl.src);
      }
    }
  });
  
  window._videoGenEventsInitialized = true;
}

// Global Export
window.openVideoGenModal = openVideoGenModal;
window.openVideoPreview = openVideoPreview;

// Debug utility: Reset UI state if something gets stuck
window.resetVideoGenUI = function() {
  // Close all video-related modals
  document.querySelectorAll('.modal-overlay.active').forEach(modal => {
    if (modal.id === 'video-gen-modal' || modal.id === 'video-preview-modal') {
      modal.classList.remove('active');
    }
  });

  // Reset body state
  document.body.style.overflow = '';
  document.body.style.userSelect = '';
  document.body.style.cursor = '';

  // Clear all polling intervals
  Object.keys(pollingIntervals).forEach(key => {
    clearInterval(pollingIntervals[key]);
    delete pollingIntervals[key];
  });

  log.debug(' Video generation UI state reset');
  console.log('[VideoGen] UI state has been reset');
};
